import type { LiceFilterClass, WelfareFilterClass } from '@restDataAccess';

import { FilterType, type WelfareFishCandidate, WelfareType } from '@scaleaq/sitecontrol-lib';

import { FishWelfareResponse } from './fishObservationGallery.types';

export const observationBreakdownTranslationKeys = {
  [FishWelfareResponse.Female]: 'liceCount.imageGallery.metadata.observationBreakdown.adultFemale',
  [FishWelfareResponse.FemaleEggstring]:
    'liceCount.imageGallery.metadata.observationBreakdown.adultFemaleEggString',
  [FishWelfareResponse.Mobile]: 'liceCount.imageGallery.metadata.observationBreakdown.mobile',
  [FishWelfareResponse.ScottishLice]:
    'liceCount.imageGallery.metadata.observationBreakdown.caligus',
  [FishWelfareResponse.ActiveWound]:
    'liceCount.imageGallery.metadata.observationBreakdown.activeWound',
  [FishWelfareResponse.HealedWound]:
    'liceCount.imageGallery.metadata.observationBreakdown.healedWound',
  [FishWelfareResponse.ScaleLoss]: 'liceCount.imageGallery.metadata.observationBreakdown.scaleLoss',
  [FishWelfareResponse.SnoutDamage]:
    'liceCount.imageGallery.metadata.observationBreakdown.snoutDamage',
} as const;

export type ObservationBreakdownCategory = keyof typeof observationBreakdownTranslationKeys;

/** Detections of one category, split by welfare score when the results carry one. */
export type ObservationBreakdownEntry = {
  category: ObservationBreakdownCategory;
  score?: number;
  count: number;
};

/**
 * Selectable lice filter classes, in display order. The enum values double as the
 * `liceCount.imageGallery.filterClasses.*` i18n key suffixes.
 */
export const liceFilterClasses: LiceFilterClass[] = [
  FilterType.Mobile,
  FilterType.Female,
  FilterType.FemaleEggstring,
  FilterType.ScottishLice,
];

/** Chips offered in the lice gallery: the lice classes plus scale loss, which is off by default. */
export const liceGalleryFilterClasses: (LiceFilterClass | WelfareFilterClass)[] = [
  ...liceFilterClasses,
  WelfareType.Scaleloss,
];

export const welfareFilterClasses: WelfareFilterClass[] = [
  WelfareType.Wounds,
  WelfareType.Scaleloss,
  WelfareType.Snoutdamage,
];

export const fishWelfareResponseMapping: Record<
  FishWelfareResponse,
  LiceFilterClass | WelfareFilterClass
> = {
  [FishWelfareResponse.Female]: FilterType.Female,
  [FishWelfareResponse.FemaleEggstring]: FilterType.FemaleEggstring,
  [FishWelfareResponse.Mobile]: FilterType.Mobile,
  [FishWelfareResponse.ScottishLice]: FilterType.ScottishLice,
  [FishWelfareResponse.ActiveWound]: WelfareType.Wounds,
  [FishWelfareResponse.HealedWound]: WelfareType.Wounds,
  [FishWelfareResponse.ScaleLoss]: WelfareType.Scaleloss,
  [FishWelfareResponse.SnoutDamage]: WelfareType.Snoutdamage,
};

/** Welfare types whose results all count toward one breakdown category, in caption order. */
export const singleCategoryWelfareTypes = [
  {
    welfareType: WelfareType.Scaleloss,
    welfareKey: 'scaleloss',
    category: FishWelfareResponse.ScaleLoss,
  },
  {
    welfareType: WelfareType.Snoutdamage,
    welfareKey: 'snoutdamage',
    category: FishWelfareResponse.SnoutDamage,
  },
] as const satisfies {
  welfareType: WelfareFilterClass;
  welfareKey: keyof WelfareFishCandidate['welfare'];
  category: ObservationBreakdownCategory;
}[];

export const welfareFilterMapping: Record<WelfareType, FilterType[]> = {
  [WelfareType.Lice]: [
    FilterType.Female,
    FilterType.FemaleEggstring,
    FilterType.Mobile,
    FilterType.ScottishLice,
  ],
  [WelfareType.Wounds]: [FilterType.ActiveWound, FilterType.HealedWound],
  [WelfareType.Snoutdamage]: [FilterType.SnoutDamage],
  [WelfareType.Fins]: [
    FilterType.Dorsal,
    FilterType.Pectoral,
    FilterType.Pelvic,
    FilterType.Anal,
    FilterType.Caudal,
  ],
  [WelfareType.Eye]: [FilterType.Cataract, FilterType.EyeBleeding, FilterType.Exopthalmous],
  [WelfareType.Deformities]: [
    FilterType.Spine,
    FilterType.GillDamage,
    FilterType.UpperJaw,
    FilterType.LowerJaw,
  ],
  [WelfareType.Maturation]: [FilterType.Maturation],
  [WelfareType.Scaleloss]: [FilterType.ScaleLoss],
};
